import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Mail, Send, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';
import { apiErrorMessage } from '../lib/apiError';
import { formatDateTime } from '../lib/dateFormat';

/**
 * Email Campaigns
 * --------------------------------------------------------------------------
 * Tab 1 — Compose: pick category, event type, subject + body, channels
 *   (Email/SMS), paste a recipient list and/or "all customers", send (or
 *   dry-run for a head-count). One-shot bulk send via Brevo + D7.
 * Tab 2 — Auto-triggers: per-category toggles that fire a campaign to the
 *   whole customer list whenever a product in that category flips stock.
 *   Four toggles per category (back-in-stock Email/SMS, out-of-stock
 *   Email/SMS). Defaults are OFF so categories don't start spamming.
 * Tab 3 — History: last 20 campaign jobs with status + counts.
 */
const TABS = [
  { key: 'compose', label: 'Compose campaign' },
  { key: 'auto', label: 'Auto-triggers' },
  { key: 'history', label: 'History' },
];

const EVENT_OPTIONS = [
  { value: 'custom', label: 'Custom announcement' },
  { value: 'back_in_stock', label: 'Back in stock' },
  { value: 'out_of_stock', label: 'Out of stock' },
  { value: 'stock_update', label: 'Stock update (in + out)' },
];

const DEFAULTS_BY_EVENT = {
  back_in_stock: {
    subject: 'Good news — these items are back in stock',
    body: 'Hi there,\n\nGreat news! Items in {category} are available again. Visit our store and place your order before they sell out.\n\n— Mohammed Riaz & Partner LLC',
  },
  out_of_stock: {
    subject: 'Heads-up — items currently out of stock',
    body: 'Hi there,\n\nA quick note that some items in {category} are temporarily out of stock. We will let you know the moment they return.\n\n— Mohammed Riaz & Partner LLC',
  },
  stock_update: {
    subject: 'Stock update — what is in and out today',
    body: 'Hi there,\n\nA quick stock update for {category}. Some items just came back into stock while a few others are temporarily unavailable. Visit our store for the latest availability.\n\n— Mohammed Riaz & Partner LLC',
  },
  custom: { subject: '', body: '' },
};

const EmailCampaigns = () => {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();

  const [tab, setTab] = useState('compose');
  const [categories, setCategories] = useState([]);

  // Compose state
  const [categorySlug, setCategorySlug] = useState('');
  const [eventType, setEventType] = useState('custom');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [channels, setChannels] = useState({ email: true, sms: false });
  const [recipientsText, setRecipientsText] = useState('');
  const [allCustomers, setAllCustomers] = useState(false);
  const [sending, setSending] = useState(false);
  const [dryRunInfo, setDryRunInfo] = useState(null);

  // Auto-trigger save state — keyed by slug, only dirty rows are persisted.
  const [autoSaving, setAutoSaving] = useState({});

  // History
  const [history, setHistory] = useState([]);

  // Global email-body visibility toggles (View&order CTA, Browse-alternatives
  // CTA, price line). Stored under settings.notifications.campaign_show_*.
  const [visTogs, setVisTogs] = useState({
    campaign_show_back_cta: true,
    campaign_show_out_cta: true,
    campaign_show_price: true,
    campaign_show_image: true,
    campaign_show_pack: true,
  });
  // Auto-trigger settings: recipients list, include-all toggle, and the
  // per-campaign sender-ID overrides (one for email From-name, one for the
  // D7 SMS alphanumeric originator).
  const [autoCfg, setAutoCfg] = useState({
    campaign_auto_recipients_text: '',
    campaign_auto_include_all_customers: true,
    campaign_sender_name: 'MR Supply Chain',
    campaign_sms_sender_id: '',
    campaign_footer_text: 'CENTRAL WAREHOUSE MANAGEMENT',
    campaign_footer_align: 'left',
  });
  const [autoCfgDirty, setAutoCfgDirty] = useState(false);
  const [autoCfgSaving, setAutoCfgSaving] = useState(false);
  const [visSaving, setVisSaving] = useState(false);
  const [settingsDoc, setSettingsDoc] = useState(null);

  // Per-category recipients text — kept locally so the user can type without
  // refetching the whole categories list. Dirty rows show a Save button.
  const [catRecipients, setCatRecipients] = useState({}); // { slug: text }
  const [catRecipientsDirty, setCatRecipientsDirty] = useState({}); // { slug: bool }
  const [expandedCat, setExpandedCat] = useState(null);

  const loadCategories = async () => {
    try {
      // Admin endpoint — returns ALL categories including hidden ones.
      // Email campaigns target internal/admin recipient lists, so even
      // categories not shown on the public storefront need to appear here.
      const { data } = await axios.get(`${API}/admin/categories`, { headers: authHeaders });
      setCategories(data || []);
      const initial = {};
      (data || []).forEach((c) => { initial[c.slug] = c.auto_recipients_text || ''; });
      setCatRecipients(initial);
      setCatRecipientsDirty({});
      if (!categorySlug && (data || []).length) setCategorySlug(data[0].slug);
    } catch (err) {
      toast({ title: 'Failed to load divisions', description: apiErrorMessage(err) });
    }
  };

  const saveCatRecipients = async (cat) => {
    try {
      await axios.put(
        `${API}/admin/categories/${cat.id}`,
        { auto_recipients_text: catRecipients[cat.slug] || '' },
        { headers: authHeaders }
      );
      setCategories((list) => list.map((c) => c.slug === cat.slug ? { ...c, auto_recipients_text: catRecipients[cat.slug] || '' } : c));
      setCatRecipientsDirty((d) => { const n = { ...d }; delete n[cat.slug]; return n; });
      toast({ title: 'Saved' });
    } catch (err) {
      toast({ title: 'Save failed', description: apiErrorMessage(err) });
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/campaigns`, { headers: authHeaders });
      setHistory(data || []);
    } catch (err) {
      toast({ title: 'Failed to load history', description: apiErrorMessage(err) });
    }
  };

  const loadVisibilityToggles = async () => {
    // PUT /api/admin/settings replaces the entire doc, so we hold a copy
    // of the full settings payload and mutate just the notifications keys
    // we care about — keeps the rest of the admin's settings untouched.
    try {
      const { data } = await axios.get(`${API}/admin/settings`, { headers: authHeaders });
      setSettingsDoc(data || null);
      const n = (data && data.notifications) || {};
      setVisTogs({
        campaign_show_back_cta: n.campaign_show_back_cta !== false,
        campaign_show_out_cta: n.campaign_show_out_cta !== false,
        campaign_show_price: n.campaign_show_price !== false,
        campaign_show_image: n.campaign_show_image !== false,
      });
      setAutoCfg({
        campaign_auto_recipients_text: n.campaign_auto_recipients_text || '',
        campaign_auto_include_all_customers: n.campaign_auto_include_all_customers !== false,
        campaign_sender_name: n.campaign_sender_name || 'MR Supply Chain',
        campaign_sms_sender_id: n.campaign_sms_sender_id || '',
      });
      setAutoCfgDirty(false);
    } catch (err) {
      // Non-fatal — defaults stay true.
    }
  };

  const saveAutoCfg = async () => {
    setAutoCfgSaving(true);
    try {
      const nextSettings = {
        ...(settingsDoc || {}),
        notifications: {
          ...((settingsDoc && settingsDoc.notifications) || {}),
          ...visTogs,
          ...autoCfg,
        },
      };
      const { data } = await axios.put(`${API}/admin/settings`, nextSettings, { headers: authHeaders });
      setSettingsDoc(data || nextSettings);
      setAutoCfgDirty(false);
      toast({ title: 'Auto-trigger config saved' });
    } catch (err) {
      toast({ title: 'Save failed', description: apiErrorMessage(err) });
    } finally {
      setAutoCfgSaving(false);
    }
  };

  const patchAutoCfg = (patch) => {
    setAutoCfg((s) => ({ ...s, ...patch }));
    setAutoCfgDirty(true);
  };

  const saveVisibilityToggle = async (key, value) => {
    const prev = visTogs[key];
    setVisTogs((s) => ({ ...s, [key]: value }));
    setVisSaving(true);
    try {
      const nextSettings = {
        ...(settingsDoc || {}),
        notifications: {
          ...((settingsDoc && settingsDoc.notifications) || {}),
          ...visTogs,
          [key]: value,
        },
      };
      const { data } = await axios.put(`${API}/admin/settings`, nextSettings, { headers: authHeaders });
      setSettingsDoc(data || nextSettings);
    } catch (err) {
      setVisTogs((s) => ({ ...s, [key]: prev }));
      toast({ title: 'Save failed', description: apiErrorMessage(err) });
    } finally {
      setVisSaving(false);
    }
  };

  useEffect(() => {
    loadCategories();
    loadHistory();
    loadVisibilityToggles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply the event-type default copy when admin first picks one (without
  // overwriting their own edits).
  const applyDefaults = (next) => {
    setEventType(next);
    const d = DEFAULTS_BY_EVENT[next] || DEFAULTS_BY_EVENT.custom;
    const catName = (categories.find((c) => c.slug === categorySlug) || {}).name || 'the category';
    if (!subject.trim() && d.subject) setSubject(d.subject);
    if (!body.trim() && d.body) setBody(d.body.replace('{category}', catName));
  };

  const buildPayload = (dry) => ({
    category_slug: categorySlug || null,
    event_type: eventType,
    subject: subject.trim(),
    body_text: body,
    body_html: `<div style="font-family:Arial,Helvetica,sans-serif;white-space:pre-wrap;color:#0F172A;max-width:560px;margin:0 auto;padding:24px;">${body
      .split('\n')
      .map((l) => l.replace(/</g, '&lt;'))
      .join('<br/>')}</div>`,
    channels: Object.entries(channels).filter(([, v]) => v).map(([k]) => k),
    recipients_text: recipientsText,
    send_to_all_customers: allCustomers,
    dry_run: !!dry,
  });

  const submit = async (dry) => {
    if (!subject.trim()) {
      toast({ title: 'Subject required' });
      return;
    }
    if (!Object.values(channels).some(Boolean)) {
      toast({ title: 'Pick at least one channel' });
      return;
    }
    if (!recipientsText.trim() && !allCustomers) {
      toast({ title: 'Pick recipients', description: 'Paste an email/phone list or tick "All customers".' });
      return;
    }
    setSending(true);
    setDryRunInfo(null);
    try {
      const { data } = await axios.post(`${API}/admin/campaigns/send`, buildPayload(dry), { headers: authHeaders });
      if (dry) {
        setDryRunInfo(data);
        toast({ title: 'Dry-run complete', description: `${data.email_count} emails + ${data.sms_count} SMS would be sent.` });
      } else {
        toast({ title: 'Campaign queued', description: `${data.email_count} emails + ${data.sms_count} SMS. See History.` });
        setSubject(''); setBody(''); setRecipientsText(''); setAllCustomers(false);
        loadHistory();
      }
    } catch (err) {
      toast({ title: 'Send failed', description: apiErrorMessage(err) });
    } finally {
      setSending(false);
    }
  };

  const toggleAuto = async (cat, field, value) => {
    const slug = cat.slug;
    // The backend PUT route is keyed by category UUID, not slug. The
    // GET /categories payload includes both.
    const id = cat.id;
    setAutoSaving((s) => ({ ...s, [`${slug}:${field}`]: true }));
    try {
      await axios.put(`${API}/admin/categories/${id}`, { [field]: value }, { headers: authHeaders });
      setCategories((list) => list.map((c) => c.slug === slug ? { ...c, [field]: value } : c));
    } catch (err) {
      toast({ title: 'Save failed', description: apiErrorMessage(err) });
    } finally {
      setAutoSaving((s) => { const n = { ...s }; delete n[`${slug}:${field}`]; return n; });
    }
  };

  const recipientsResolvedHint = useMemo(() => {
    const parts = recipientsText
      .split(/[\s,;]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    const emails = parts.filter((p) => p.includes('@')).length;
    const phones = parts.filter((p) => !p.includes('@')).length;
    return { emails, phones, total: parts.length };
  }, [recipientsText]);

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto p-5 md:p-8">
        <div className="flex items-center gap-3 mb-1">
          <Mail className="w-6 h-6 text-[#0078D4]" />
          <h1 className="text-2xl font-bold tracking-tight text-neutral-900">Email Campaigns</h1>
        </div>
        <p className="text-sm text-neutral-500 mb-6">
          Send Email + SMS to your customer list — one-shot announcements or per-division auto-triggers on stock flips.
        </p>

        <div className="flex gap-1.5 mb-6 border-b border-neutral-200">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`campaigns-tab-${t.key}`}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === t.key ? 'border-[#0078D4] text-[#0078D4]' : 'border-transparent text-neutral-500 hover:text-neutral-800'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'compose' && (
          <div className="space-y-5 bg-white border border-neutral-200 rounded-xl p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Category (optional)</label>
                <select
                  value={categorySlug}
                  onChange={(e) => setCategorySlug(e.target.value)}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-neutral-300 bg-white text-sm"
                  data-testid="campaign-category-select"
                >
                  <option value="">— All divisions —</option>
                  {categories.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.name}</option>
                  ))}
                </select>
                <p className="text-[11px] text-neutral-500 mt-1">Tags the campaign for the audit log. Recipients come from the list below — division does not filter who receives the email.</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Event type</label>
                <select
                  value={eventType}
                  onChange={(e) => applyDefaults(e.target.value)}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-neutral-300 bg-white text-sm"
                  data-testid="campaign-event-select"
                >
                  {EVENT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <p className="text-[11px] text-neutral-500 mt-1">Sets a starting subject/body template you can tweak.</p>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Subject</label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" placeholder="e.g. Back in stock: Glace Strawberry" data-testid="campaign-subject" />
            </div>

            <div>
              <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Body</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                className="mt-1 w-full px-3 py-2 rounded-md border border-neutral-300 bg-white text-sm font-mono"
                placeholder="Plain text — line breaks preserved in email. Also used as the SMS body (truncated to 320 chars)."
                data-testid="campaign-body"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Channels</label>
              <div className="mt-2 flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={channels.email} onCheckedChange={(v) => setChannels((s) => ({ ...s, email: v }))} data-testid="campaign-channel-email" />
                  <span>Email (Brevo)</span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={channels.sms} onCheckedChange={(v) => setChannels((s) => ({ ...s, sms: v }))} data-testid="campaign-channel-sms" />
                  <span>SMS (D7)</span>
                </label>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Recipients list</label>
              <textarea
                value={recipientsText}
                onChange={(e) => setRecipientsText(e.target.value)}
                rows={5}
                className="mt-1 w-full px-3 py-2 rounded-md border border-neutral-300 bg-white text-sm font-mono"
                placeholder={'Paste emails and/or phones, one per line or comma-separated.\nExample:\nahmed@example.com\n+96891234567\nfatma@example.com'}
                data-testid="campaign-recipients"
              />
              <div className="mt-1 flex items-center justify-between text-[11px] text-neutral-500">
                <span>Parsed: {recipientsResolvedHint.emails} emails · {recipientsResolvedHint.phones} phones</span>
                <label className="flex items-center gap-2 text-xs">
                  <Switch checked={allCustomers} onCheckedChange={setAllCustomers} data-testid="campaign-all-customers" />
                  <span>Also include all registered customers</span>
                </label>
              </div>
            </div>

            {dryRunInfo && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Dry-run resolved: <strong>{dryRunInfo.email_count}</strong> emails · <strong>{dryRunInfo.sms_count}</strong> SMS. No send yet.
              </div>
            )}

            <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-neutral-200">
              <Button variant="outline" disabled={sending} onClick={() => submit(true)} data-testid="campaign-dry-run">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Dry-run'}
              </Button>
              <Button disabled={sending} onClick={() => submit(false)} className="bg-[#0078D4] hover:bg-[#005A9E]" data-testid="campaign-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Send campaign</>}
              </Button>
            </div>
          </div>
        )}

        {tab === 'auto' && (
          <div className="space-y-4">
            <div className="bg-white border border-neutral-200 rounded-xl p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-neutral-700 mb-2">Auto-trigger recipients &amp; sender</div>
              <p className="text-xs text-neutral-500 mb-3">Applies to every <strong>auto-triggered</strong> back/out-of-stock email + SMS. Manual sends in the Compose tab ignore these.</p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Recipients list</label>
                  <textarea
                    value={autoCfg.campaign_auto_recipients_text}
                    onChange={(e) => patchAutoCfg({ campaign_auto_recipients_text: e.target.value })}
                    rows={4}
                    className="mt-1 w-full px-3 py-2 rounded-md border border-neutral-300 bg-white text-sm font-mono"
                    placeholder={'Paste emails / phones, one per line.\nExample:\nbuyers@horeca.om\n+96891234567'}
                    data-testid="auto-recipients"
                  />
                  <label className="mt-2 flex items-center gap-2 text-xs text-neutral-700">
                    <Switch
                      checked={autoCfg.campaign_auto_include_all_customers}
                      onCheckedChange={(v) => patchAutoCfg({ campaign_auto_include_all_customers: v })}
                      data-testid="auto-include-all"
                    />
                    <span>Also include all registered customers</span>
                  </label>
                  <p className="text-[11px] text-neutral-500 mt-1">
                    If the list is empty AND this toggle is off, auto-triggers won&#39;t send to anyone (silently skipped).
                  </p>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Email sender name (From)</label>
                    <Input
                      value={autoCfg.campaign_sender_name}
                      onChange={(e) => patchAutoCfg({ campaign_sender_name: e.target.value })}
                      placeholder="MR Supply Chain"
                      className="mt-1"
                      data-testid="auto-sender-name"
                    />
                    <p className="text-[11px] text-neutral-500 mt-1">Shown as the From-name in the recipient&#39;s inbox (the From-address stays the same).</p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">SMS sender ID (D7 originator)</label>
                    <Input
                      value={autoCfg.campaign_sms_sender_id}
                      onChange={(e) => patchAutoCfg({ campaign_sms_sender_id: e.target.value })}
                      placeholder="(leave blank to use the default MRPCO)"
                      className="mt-1"
                      data-testid="auto-sms-sender"
                    />
                    <p className="text-[11px] text-neutral-500 mt-1">Must be pre-registered with D7 / your carrier — unregistered IDs are silently rewritten.</p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Email footer text</label>
                    <div className="mt-1 flex items-stretch gap-1.5">
                      <select
                        value={autoCfg.campaign_footer_align || 'left'}
                        onChange={(e) => patchAutoCfg({ campaign_footer_align: e.target.value })}
                        className="h-10 px-2 rounded-md border border-neutral-300 bg-neutral-50 text-xs font-semibold uppercase tracking-wide text-neutral-700"
                        data-testid="auto-footer-align"
                        aria-label="Footer alignment"
                      >
                        <option value="left">Left</option>
                        <option value="right">Right</option>
                        <option value="center">Center</option>
                      </select>
                      <Input
                        value={autoCfg.campaign_footer_text}
                        onChange={(e) => patchAutoCfg({ campaign_footer_text: e.target.value })}
                        placeholder="CENTRAL WAREHOUSE MANAGEMENT"
                        className="flex-1"
                        data-testid="auto-footer-text"
                      />
                    </div>
                    <p className="text-[11px] text-neutral-500 mt-1">Shown as a small upper-case caption at the bottom of every auto-triggered email. Leave the text blank to hide it.</p>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <Button
                  onClick={saveAutoCfg}
                  disabled={!autoCfgDirty || autoCfgSaving}
                  className="bg-[#0078D4] hover:bg-[#005A9E]"
                  data-testid="auto-cfg-save"
                >
                  {autoCfgSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save changes'}
                </Button>
              </div>
            </div>

            <div className="bg-white border border-neutral-200 rounded-xl p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-neutral-700 mb-2">Email body visibility</div>
              <p className="text-xs text-neutral-500 mb-3">Hide or show these blocks in every auto-triggered email. Manual campaigns ignore these (admin writes the body directly).</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { k: 'campaign_show_image', label: 'Show product image' },
                  { k: 'campaign_show_price', label: 'Show product price line (OMR x.xxx)' },
                  { k: 'campaign_show_back_cta', label: 'Show "View & order now" button (back-in-stock)' },
                  { k: 'campaign_show_out_cta', label: 'Show "Browse alternatives" button (out-of-stock)' },
                ].map(({ k, label }) => (
                  <label key={k} className="flex items-start gap-3 p-3 rounded-lg border border-neutral-200 hover:bg-neutral-50 cursor-pointer">
                    <Switch
                      checked={!!visTogs[k]}
                      onCheckedChange={(v) => saveVisibilityToggle(k, v)}
                      disabled={visSaving}
                      data-testid={`toggle-${k}`}
                    />
                    <span className="text-sm text-neutral-700 leading-snug">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-neutral-200 text-sm text-neutral-700">
                Per-category controls. Toggle which stock events trigger an auto-campaign, and (optionally) set a recipients list that overrides the global one above. Empty per-category list ⇒ falls back to the global recipients.
              </div>
              <div className="divide-y divide-neutral-100">
                {categories.map((c) => {
                  const isOpen = expandedCat === c.slug;
                  const hasOwnList = !!(catRecipients[c.slug] || '').trim();
                  return (
                    <div key={c.slug} className="px-4 py-3" data-testid={`category-row-${c.slug}`}>
                      <div className="flex flex-wrap items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setExpandedCat(isOpen ? null : c.slug)}
                          className="font-medium text-neutral-800 text-left flex items-center gap-1.5 hover:text-[#0078D4]"
                          data-testid={`category-expand-${c.slug}`}
                        >
                          <span className={`inline-block transition-transform ${isOpen ? 'rotate-90' : ''}`}>▸</span>
                          {c.name}
                          {hasOwnList && (
                            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0078D4]/10 text-[#0078D4] text-[10px] font-bold uppercase tracking-wide">
                              own list
                            </span>
                          )}
                        </button>
                        <div className="ml-auto flex flex-wrap items-center gap-3 text-xs text-neutral-500">
                          {[
                            { f: 'auto_back_in_stock_email', label: 'Back · Email' },
                            { f: 'auto_back_in_stock_sms', label: 'Back · SMS' },
                            { f: 'auto_out_of_stock_email', label: 'Out · Email' },
                            { f: 'auto_out_of_stock_sms', label: 'Out · SMS' },
                          ].map(({ f, label }) => (
                            <label key={f} className="flex items-center gap-1.5">
                              <Switch
                                checked={!!c[f]}
                                onCheckedChange={(v) => toggleAuto(c, f, v)}
                                disabled={!!autoSaving[`${c.slug}:${f}`]}
                                data-testid={`auto-${f}-${c.slug}`}
                              />
                              <span>{label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      {isOpen && (
                        <div className="mt-3 pl-5 border-l-2 border-[#0078D4]/40">
                          <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Recipients list for this division</label>
                          <textarea
                            value={catRecipients[c.slug] ?? ''}
                            onChange={(e) => {
                              setCatRecipients((m) => ({ ...m, [c.slug]: e.target.value }));
                              setCatRecipientsDirty((d) => ({ ...d, [c.slug]: true }));
                            }}
                            rows={3}
                            className="mt-1 w-full px-3 py-2 rounded-md border border-neutral-300 bg-white text-sm font-mono"
                            placeholder={'Emails / phones, one per line.\nLeave blank to use the global list above.'}
                            data-testid={`category-recipients-${c.slug}`}
                          />
                          <div className="mt-2 flex justify-end">
                            <Button
                              onClick={() => saveCatRecipients(c)}
                              disabled={!catRecipientsDirty[c.slug]}
                              size="sm"
                              className="bg-[#0078D4] hover:bg-[#005A9E]"
                              data-testid={`category-recipients-save-${c.slug}`}
                            >
                              Save list
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {!categories.length && (
                  <div className="p-6 text-center text-neutral-400">No divisions yet.</div>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === 'history' && (
          <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left p-3">When</th>
                  <th className="text-left p-3">Subject</th>
                  <th className="p-3">Trigger</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Email</th>
                  <th className="p-3">SMS</th>
                </tr>
              </thead>
              <tbody>
                {history.map((j) => (
                  <tr key={j.id} className="border-t border-neutral-100">
                    <td className="p-3 text-neutral-500 whitespace-nowrap">{formatDateTime(j.created_at)}</td>
                    <td className="p-3 text-neutral-800">{j.subject}</td>
                    <td className="p-3 text-center text-xs uppercase tracking-wide text-neutral-500">{j.trigger || 'manual'}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${j.status === 'done' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{j.status}</span>
                    </td>
                    <td className="p-3 text-center text-neutral-700">{j.sent_email || 0}/{j.email_count || 0}{j.failed_email ? ` (${j.failed_email} failed)` : ''}</td>
                    <td className="p-3 text-center text-neutral-700">{j.sent_sms || 0}/{j.sms_count || 0}{j.failed_sms ? ` (${j.failed_sms} failed)` : ''}</td>
                  </tr>
                ))}
                {!history.length && (
                  <tr><td colSpan={6} className="p-8 text-center text-neutral-400 flex items-center justify-center gap-2"><AlertCircle className="w-4 h-4" /> No campaigns sent yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default EmailCampaigns;

